/**
 * What a founder is told when an Honorable Mention is refused.
 *
 * Keyed on the **code**, for the reason `./bid-messages.ts` gives: core's own messages are
 * contract text written for every caller, and some describe conditions ("Send
 * application/json") a founder can do nothing about. The code is the stable part of the
 * contract (`contracts/API.md` § Honorable Mention shape), so the wording lives here.
 *
 * Every refusal below names one condition the founder can act on, and none of them softens
 * canon § 10. In particular `DEAL_ALREADY_BIDDING` and `DEAL_ALREADY_PLACED` are not the
 * same sentence: one deal, one position, one day means a founder who is already competing
 * for the Top 10 is told what they hold rather than being sold a second listing.
 */
import { isNetworkError } from "../api/client";
import type { ApiError } from "../api/client";

const MESSAGES: Record<string, string> = {
  // Canon § 10: a mention is subject to the same approval gate as every other placement.
  DEAL_NOT_ELIGIBLE_FOR_PLACEMENT:
    "This deal can't be listed until Dealbid has approved it. Its review state is on your deals panel.",
  // Canon § 7: a deal takes at most one position a day, so this is a statement of what the
  // founder already has, not an obstacle to work around.
  DEAL_ALREADY_PLACED:
    "This deal already has a position on tomorrow's board. A deal takes one position a day.",
  DEAL_ALREADY_BIDDING:
    "This deal is bidding for tomorrow's Top 10. A deal takes one position a day, so withdraw from tomorrow's auction or list a different deal.",
  HONORABLE_MENTION_DAY_CLOSED:
    "Tomorrow's board has already been decided, so it can't take another listing. The next day opens when that board is finalised.",
  INSUFFICIENT_PROMOTIONAL_CREDIT:
    "You don't have enough available promotional credit for an Honorable Mention. Credit held by a standing bid returns to available if that bid does not win.",
  PLACEMENT_CONFLICT: "Tomorrow's board changed while this was being bought. Try again.",
  NOT_FOUND: "Dealbid no longer has this deal on your account. Reload your desk.",
  VALIDATION_FAILED: "Dealbid didn't accept that request. Reload your desk and try again.",
  MALFORMED_RESPONSE:
    "Dealbid didn't answer as expected. The listing was not bought and your credit was not touched.",
};

/** The generic. Honest about the two things actually known: it failed, and retrying is fair. */
const GENERIC = "Dealbid couldn't buy that listing. Try again in a moment.";

const OFFLINE =
  "Dealbid couldn't be reached, so the listing was not bought. Check your connection and try again.";

export function messageForMentionError(error: ApiError): string {
  // A malformed body also carries a null status — the request completed and the answer was
  // unreadable — so the code is checked first. Telling a founder their connection dropped
  // when it did not would send them to restart a router that is working.
  if (error.code !== null) return MESSAGES[error.code] ?? GENERIC;
  return isNetworkError(error) ? OFFLINE : GENERIC;
}
